import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Newspaper, Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import DataTable from "@/components/app/DataTable";
import Modal from "@/components/app/Modal";
import ImagePicker from "@/components/homepage/ImagePicker";
import { Field, useValidatedForm } from "@/components/app/FormKit";
import { btnOutline, btnPrimary, iconBtn, pageSub, pageTitle, fmtDate } from "@/components/app/ui";

const empty = { title: "", excerpt: "", body: "", cover: "", author: "", status: "draft" };
const STATUS_OPTS = [{ value: "published", label: "Published" }, { value: "draft", label: "Draft" }];

export default function Blog() {
  const [storeSlug, setStoreSlug] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/vendor/blog").then(({ data }) => setItems(data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); api.get("/vendor/store").then(({ data }) => setStoreSlug(data.slug || "")).catch(() => {}); }, [load]);

  const validate = useCallback((f) => {
    const e = {};
    const title = f.title.trim();
    if (!title) e.title = "Title is required.";
    else if (title.length < 2) e.title = "Title must be at least 2 characters.";
    if (f.status === "published" && !f.body.trim()) e.body = "Write the post before publishing it.";
    return e;
  }, []);

  const form = useValidatedForm(empty, validate);
  const { bind } = form;

  const openNew = () => { form.reset(empty); setEditId(null); setOpen(true); };
  const openEdit = async (p) => {
    try {
      const { data } = await api.get(`/vendor/blog/${p.id}`);
      form.reset({ ...empty, ...data, excerpt: data.excerpt || "", body: data.body || "", cover: data.cover || "", author: data.author || "" });
      setEditId(p.id); setOpen(true);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.check()) return;
    setSaving(true);
    const v = form.values;
    const payload = { ...v, title: v.title.trim() };
    try {
      if (editId) await api.put(`/vendor/blog/${editId}`, payload);
      else await api.post("/vendor/blog", payload);
      toast.success(`Post “${payload.title}” ${editId ? "updated" : payload.status === "published" ? "published" : "saved as draft"}.`);
      setOpen(false); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete post “${p.title}”?`)) return;
    try { await api.delete(`/vendor/blog/${p.id}`); toast.success("Post deleted."); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const columns = useMemo(() => [
    { key: "title", header: "Post", cell: (p) => (
      <div className="flex items-center gap-3 min-w-0">
        {p.cover ? <img src={p.cover} alt="" className="w-12 h-9 rounded-md object-cover border border-slate-200 shrink-0" /> : <span className="w-12 h-9 rounded-md bg-slate-100 shrink-0" />}
        <span className="font-semibold text-neutral-900 truncate">{p.title}</span>
      </div>) },
    { key: "author", header: "Author", hideBelow: "md", cell: (p) => <span className="text-slate-600">{p.author || "—"}</span> },
    { key: "status", header: "Status", accessor: (p) => p.status, cell: (p) => (
      <span className={`inline-block text-[11px] px-2 py-0.5 rounded-md border font-medium capitalize ${p.status === "published" ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{p.status}</span>) },
    { key: "date", header: "Date", hideBelow: "sm", accessor: (p) => p.published_at || p.created_at, cell: (p) => <span className="text-slate-600">{fmtDate(p.published_at || p.created_at)}</span> },
    { key: "actions", header: "", sortable: false, align: "right", cell: (p) => (
      <div className="flex justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
        {p.status === "published" && storeSlug && (
          <a href={`/store/${storeSlug}/blogs/${p.slug}`} target="_blank" rel="noreferrer" className={`${iconBtn} hover:bg-slate-100 hover:text-slate-800`} aria-label={`View ${p.title}`}><ExternalLink size={14} /></a>
        )}
        <button onClick={() => openEdit(p)} data-testid={`blog-edit-${p.id}`} className={`${iconBtn} hover:bg-blue-50 hover:text-blue-600`} aria-label={`Edit ${p.title}`}><Pencil size={14} /></button>
        <button onClick={() => del(p)} data-testid={`blog-delete-${p.id}`} className={`${iconBtn} hover:bg-red-50 hover:text-red-600`} aria-label={`Delete ${p.title}`}><Trash2 size={14} /></button>
      </div>) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [storeSlug]);

  const filters = useMemo(() => [{ key: "status", label: "All status", accessor: (p) => p.status, options: STATUS_OPTS }], []);

  return (
    <div className="space-y-4" data-testid="vendor-blog">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className={pageTitle}>Blog</h1>
          <p className={pageSub}>{items.length} posts. Published posts appear on the Blogs page of your store.</p>
        </div>
        <button onClick={openNew} data-testid="blog-add-btn" className={btnPrimary}><Plus size={15} /> New post</button>
      </div>

      <DataTable testId="blog-table" exportName="blog-posts" columns={columns} data={items} loading={loading} rowTestId={(p) => `blog-${p.id}`}
        searchKeys={["title", "author"]} searchPlaceholder="Search posts…" filters={filters} defaultSort={{ key: "date", dir: "desc" }} onRowClick={openEdit}
        emptyIcon={Newspaper} emptyTitle="No posts yet" emptyText="Write your first post to share news, tips and stories with your customers." />

      <Modal open={open} onClose={() => setOpen(false)} size="xl" testId="blog-modal" title={editId ? "Edit post" : "New post"} subtitle="Fields marked * are required."
        footer={<>
          <button onClick={() => setOpen(false)} className={btnOutline}>Cancel</button>
          <button onClick={save} disabled={saving} data-testid="blog-save" className={btnPrimary}>{saving ? "Saving…" : editId ? "Save changes" : "Save post"}</button>
        </>}>
        <div className="grid grid-cols-2 gap-x-3 gap-y-3">
          <Field className="col-span-2" label="Title" required maxLength={120} t="blog-title" placeholder="e.g. 5 ways to care for your sneakers" {...bind("title")} />
          <Field className="col-span-2 sm:col-span-1" label="Author" maxLength={60} t="blog-author" placeholder="Shown under the title" {...bind("author")} />
          <Field className="col-span-2 sm:col-span-1" label="Status" type="select" options={STATUS_OPTS} t="blog-status" {...bind("status")} />
          <Field className="col-span-2" label="Short summary" type="textarea" rows={2} maxLength={300} t="blog-excerpt" hint="Shown on the blog list. Leave empty to use the start of the post." {...bind("excerpt")} />
          <Field className="col-span-2" label="Post" type="textarea" rows={10} maxLength={50000} t="blog-body" hint="Plain text. Leave a blank line between paragraphs." {...bind("body")} />
          <div className="col-span-2">
            <ImagePicker label="Cover image" value={form.values.cover} onChange={(u) => form.set("cover", u)} aspect="aspect-[16/7]" t="blog-cover" hint="Optional. Wide images look best." />
          </div>
        </div>
      </Modal>
    </div>
  );
}
